import { Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import type {
    MapboxMap,
    RideType,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    ConsoleLogService,
    RideTypeService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { PointLike } from 'mapbox-gl';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
    selectActiveRideCondition,
    selectRideConditionsInView,
} from 'src/app/components/ride-condition/ride-condition.reducer';
import { AppState } from 'src/app/models/app-state';
import { RideCondition } from 'src/app/models/ride-condition';
import { MapExtents } from 'src/app/state/map/map.reducer';
import { selectMaxRideConditionsAgeInHours } from '../state/map/layer-visibility/layer-visibility.reducer';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';
import { RideConditionService } from './ride-condition.service';

@Injectable({ providedIn: 'root' })
export class RideConditionMapService extends MapContentMapService {
    override minZoom = 8;
    private rideConditions: RideCondition[];
    private rideType$: Observable<RideType | undefined>;

    constructor(
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private rideConditionService: RideConditionService,
        private store: Store<AppState>,
        rideTypeService: RideTypeService
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('rideconditions');

        this.store
            .pipe(select(selectRideConditionsInView))
            .subscribe((riders) => (this.rideConditions = riders));
        this.rideType$ = toObservable(rideTypeService.$rideType);
    }

    setSourceDataFromModel(
        mapboxMap: MapboxMap,
        rideConditions: RideCondition[]
    ): void {
        const geoJson =
            rideConditions.length > 0
                ? this.rideConditionService.rideConditionsToGeoJson(
                      rideConditions
                  )
                : undefined;
        super.setSourceData(mapboxMap, geoJson);
    }

    getRideConditionAtPoint(
        mapboxMap: MapboxMap,
        point: PointLike
    ): RideCondition | undefined {
        const features = this.mapService.getFeaturesAtPoint(
            mapboxMap,
            point,
            this.layerIds
        );
        const rideCondition = features[0];

        if (features.length === 0) {
            return undefined;
        }

        return this.rideConditions.find(
            (cachedRideCondition) =>
                cachedRideCondition.id === rideCondition.properties?.id
        );
    }

    getRideConditionsWhenMapExtentsChanges(
        mapExtents$: Observable<MapExtents>
    ): Observable<RideCondition[]> {
        const filteredMapExtents$ = mapExtents$.pipe(
            filter(
                (mapExtents) =>
                    Boolean(mapExtents) && Boolean(mapExtents.bounds)
            )
        );

        return combineLatest([
            this.store.select(selectMaxRideConditionsAgeInHours),
            this.store.select(selectActiveRideCondition),
            this.rideType$,
            filteredMapExtents$,
        ]).pipe(
            switchMap(([maxAge, activeRideCondition, rideType, mapExtents]) => {
                // if we are zoomed out too far, return an array of the active ride condition, if any
                if (mapExtents.zoom <= this.minZoom) {
                    const rideConditions = activeRideCondition
                        ? [activeRideCondition]
                        : [];
                    return of(rideConditions);
                }

                return this.rideConditionService
                    .getRideConditions(
                        mapExtents.bounds,
                        rideType || 'ORV',
                        maxAge
                    )
                    .pipe(
                        map((rideConditionsInView) => {
                            if (!activeRideCondition) {
                                return rideConditionsInView;
                            }

                            return [
                                ...rideConditionsInView.filter((condition) => {
                                    return (
                                        condition.id !== activeRideCondition.id
                                    );
                                }),
                                activeRideCondition,
                            ];
                        })
                    );
            })
        );
    }
}
